import * as Sentry from "@sentry/react";
import { Pause, Play } from "lucide-react";
import { useEffect, useState } from "react";

import { useAudioVisualizer } from "~/components/input/audio/use-audio-visualizer";
import { cn } from "~/util/cn";

export const ChatMessageUserAudio: React.FC<{
  base64OrSignedURL: string;
  transcription: string;
  className?: string;
}> = ({ base64OrSignedURL, transcription, className }) => {
  const [audioSource, setAudioSource] = useState(base64OrSignedURL);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!base64OrSignedURL.startsWith("data:audio/webm;base64")) {
      const fetchAudio = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(base64OrSignedURL);
          const blob = await response.blob();

          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result as string;
            setAudioSource(base64data);
            setIsLoading(false);
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          Sentry.captureException(error, {
            level: "error",
            extra: { label: "Error downloading audio" },
          });
          setIsLoading(false);
        }
      };

      fetchAudio();
    }
  }, [base64OrSignedURL]);

  const {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    audioData,
    togglePlay,
    seekAudio,
    currentPosition,
    formatTime,
    options: { minBrightness, maxBrightness, trackOpacity },
  } = useAudioVisualizer(audioSource, {
    bars: 40,
  });

  return (
    <div className={cn("flex flex-col items-start gap-2 py-2", className)}>
      <audio ref={audioRef} src={audioSource} preload="metadata" />

      <div className="flex w-full items-center gap-3">
        <button
          onClick={togglePlay}
          className="btn-ghost btn-sm btn-square btn"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          ) : isPlaying ? (
            <Pause width={16} />
          ) : (
            <Play width={16} />
          )}
        </button>

        <div
          className="h-8 w-full cursor-pointer overflow-hidden"
          onClick={seekAudio}
        >
          <div className="flex h-full items-center gap-0.5 sm:gap-1">
            {audioData.map((level, index) => {
              const brightness =
                minBrightness + (level / 100) * (maxBrightness - minBrightness);
              const active = index <= currentPosition;

              return (
                <div
                  key={index}
                  className="min-h-1.5 w-1 flex-shrink-0 rounded-b-lg rounded-t-lg"
                  style={{
                    height: `${level}%`,
                    backgroundColor: active
                      ? "var(--fallback-p,oklch(var(--p)))"
                      : `var(--fallback-n,oklch(var(--n) / ${trackOpacity}))`,
                    filter: `brightness(${brightness}%)`,
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="ml-auto flex items-center justify-center gap-1 text-xs font-medium text-primary">
        <span>{formatTime(currentTime)}</span>
        <span>{"•"}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <p>{transcription}</p>
    </div>
  );
};
